import axios from 'axios';
import * as ReactQuery from 'react-query';
import { ReliningImageNameEnum } from '@trace/types';
import { DocumentRequestBase } from '../../../services/documents';
import { identify, makeRequest, Service } from '../../../utils/http';
import { InferMutationOptions } from '../../../utils/types';

type UploadMachineVideoData = DocumentRequestBase & {
  file: File;
  duration?: number;
};

/*
 * Uploads a single machine video for a relining entry.
 *
 * Re-uses the exact same relining upload-url mechanism used for the machine
 * picture and the other relining media (see upload-relining-machine-picture).
 * The object is stored publicly-readable in the documents S3 bucket and a
 * stable public URL is returned, so the video can be opened later both from
 * the form and from the generated certificate through the S3 media link.
 *
 * The video is streamed as-is (no transcoding). The 2-minute length limit is
 * validated on the client before this request is ever fired (see
 * ReliningMachinePicture), so no server-side length validation is required.
 */
async function uploadReliningMachineVideoApi({
  file,
  duration,
  templateId,
  documentId,
}: UploadMachineVideoData) {
  const { signedUrl, uploadUrl } = await makeRequest(
    Service.getReliningUploadImageUrl(
      documentId,
      templateId,
      ReliningImageNameEnum.MACHINE_VIDEO,
      await identify(),
    ),
  );

  await axios.put(signedUrl, file, {
    headers: {
      'Content-Type': file.type,
    },
  });

  return {
    uploadUrl,
    fileName: file.name,
    timestamp: Date.now(),
    duration,
  };
}

function useUploadReliningMachineVideo(
  options?: InferMutationOptions<typeof uploadReliningMachineVideoApi>,
) {
  const { mutate: uploadReliningMachineVideo, ...rest } = ReactQuery.useMutation(
    'uploadReliningMachineVideo',
    uploadReliningMachineVideoApi,
    options,
  );

  return {
    uploadReliningMachineVideo,
    ...rest,
  };
}

export { useUploadReliningMachineVideo };

